import { useCallback, useEffect, useState } from 'react';
import { useGuestRegistryTelemetry } from '@apps/registry/guest/GuestRegistry.telemetry';
import { getEnabledPages } from '@apps/registry/guest/utils';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import type { DesignLayoutType } from '@graphql/generated-base';
import { useAuth } from '@shared/components/AuthProvider';
import { addRendition } from '@shared/utils/photoRendition';
import { useIsWebsiteV2 } from '@shared/hooks/websiteVersion';

interface CoupleHeaderArgs {
  eventId: string;
  firebaseId?: string;
  website: string;
  isAdmin: boolean;
  registryPhoto?: string;
  pages?: ReadonlyArray<EventPageFragment>;
  layout?: DesignLayoutType;
}

export const useCoupleHeaderController = ({ eventId, pages, registryPhoto }: CoupleHeaderArgs) => {
  const { currentUser, provideLogout } = useAuth();
  const {
    registryOpenFilePicker: openFilePickerTrack,
    editPageNote: editPageNoteTrack,
    guestReadMore: guestReadMoreTrack,
    registryPagePhotoUpdated: registryPagePhotoUpdatedTrack,
  } = useGuestRegistryTelemetry();

  const [photo, setPhoto] = useState<string | undefined>(registryPhoto);

  useEffect(() => {
    setPhoto(registryPhoto);
  }, [registryPhoto, setPhoto]);

  const handleOnUpdatePhoto = useCallback(
    previewUrl => {
      setPhoto(previewUrl);
      registryPagePhotoUpdatedTrack('Update');
      openFilePickerTrack();
    },
    [openFilePickerTrack, registryPagePhotoUpdatedTrack],
  );

  const handleDeletePhoto = useCallback(() => {
    setPhoto(undefined);
    registryPagePhotoUpdatedTrack('Delete');
  }, [registryPagePhotoUpdatedTrack]);

  const onEditPageNote = useCallback(() => {
    editPageNoteTrack(eventId);
  }, [editPageNoteTrack, eventId]);

  const onContinueReading = useCallback(() => {
    guestReadMoreTrack();
  }, [guestReadMoreTrack]);

  const onHandleLogout = () => {
    provideLogout({ eventId });
  };

  // A Website V2 site has none of the V1 pages this nav lists; its guests get a way back instead.
  const websiteV2 = useIsWebsiteV2();
  const { isWebsiteV2 } = websiteV2;
  const isNavBarVisible = !websiteV2.isLoading && !isWebsiteV2 && getEnabledPages(pages).showNavigationBar;

  return {
    handleOnUpdatePhoto,
    handleDeletePhoto,
    onEditPageNote,
    onContinueReading,
    photo: addRendition({ url: photo, renditionSize: 'small' }),
    currentUser,
    onHandleLogout,
    isNavBarVisible,
    isBackToSiteVisible: isWebsiteV2,
  };
};
